import { Component, inject, input, signal } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { CommonModule } from '@angular/common';
import { lastValueFrom } from 'rxjs';
import { TabComponent } from '../../../../components/tab/tab.component';
import { TabLayoutComponent } from '../../../../components/tab-layout/tab-layout.component';
import { WiperLoadingComponent } from '../../../../components/wiper-loading/wiper-loading.component';
import { CalendarComponent } from '../../../../components/calendar/calendar.component';
import { environment } from '../../../../../environments/environment';
import { ConfigService } from '../../../../services/config.service';

interface ActivityDateStatus {
  date: string;
  hasActivity: boolean;
}

interface DatesStatusResponse {
  success: boolean;
  data: { activities: ActivityDateStatus[] };
}

interface DailyActivity {
  workType: 'NFA' | 'Single Visit' | 'Double Visit';
  activity?: string;
  customer?: string;
  rep?: string;
  speciality?: string;
  center?: string;
}

interface ActivityDetail {
  label: string;
  value: string;
}

interface DailyActivitiesResponse {
  success: boolean;
  data: { activities: DailyActivity[] };
}

@Component({
  selector: 'app-supervisor-activities-calendar-tab',
  imports: [CommonModule, TabLayoutComponent, WiperLoadingComponent, CalendarComponent],
  providers: [
    { provide: TabComponent, useExisting: SupervisorActivitiesCalendarTabComponent },
  ],
  templateUrl: './activities-calendar-tab.component.html',
  styleUrl: './activities-calendar-tab.component.scss',
})
export class SupervisorActivitiesCalendarTabComponent extends TabComponent {
  private http = inject(HttpClient);
  private configService = inject(ConfigService);

  override name = this.dictionaryService.get('Daily Activity');

  userId = input.required<string>();

  calendarMarkedDates = signal<Set<string>>(new Set());

  activities = signal<DailyActivity[]>([]);
  activitiesLoading = signal(false);
  selectedDate = signal<string | null>(null);
  calendarLoading = signal(false);

  public async main(): Promise<void> {
    await this.loadMonthData();
  }

  async onMonthChanged(event: { year: number; month: number }): Promise<void> {
    this.selectedDate.set(null);
    this.activities.set([]);
    this.calendarMarkedDates.set(new Set());
    await this.loadMonthData(event.year, event.month);
  }

  onDaySelected(dateStr: string): void {
    this.selectedDate.set(dateStr);
    this.loadDailyActivities(dateStr);
  }

  private async loadMonthData(year?: number, month?: number): Promise<void> {
    this.calendarLoading.set(true);
    const date = (year !== undefined && month !== undefined)
      ? new Date(year, month, 1)
      : new Date();
    const y = date.getFullYear();
    const m = date.getMonth();

    const from = this.configService.formatDateTimeForApi(new Date(y, m, 1));
    const lastDay = new Date(y, m + 1, 0).getDate();
    const to = this.configService.formatDateTimeForApi(new Date(y, m, lastDay), true);

    try {
      const response = await lastValueFrom(
        this.http.get<DatesStatusResponse>(
          `${environment.apiBaseUrl}/visits/analytics/users/${this.userId()}/dates-status?from=${from}&to=${to}`,
        ),
      );
      if (response.success) {
        const marked = new Set<string>();
        for (const entry of response.data.activities) {
          if (entry.hasActivity) marked.add(entry.date);
        }
        this.calendarMarkedDates.set(marked);
      }
    } catch {
      // proceed with empty data
    } finally {
      this.calendarLoading.set(false);
    }
  }

  private loadDailyActivities(date: string): void {
    this.activitiesLoading.set(true);
    this.http
      .get<DailyActivitiesResponse>(
        `${environment.apiBaseUrl}/visits/analytics/users/${this.userId()}/daily-activities?date=${date}`,
      )
      .subscribe({
        next: (response) => {
          if (response.success) {
            this.activities.set(response.data.activities);
          }
          this.activitiesLoading.set(false);
        },
        error: () => {
          this.activities.set([]);
          this.activitiesLoading.set(false);
        },
      });
  }

  getActivityDetails(activity: DailyActivity): ActivityDetail[] {
    switch (activity.workType) {
      case 'NFA':
        return [
          { label: this.dictionaryService.get('Activity') || 'Activity', value: activity.activity || '-' },
        ];
      case 'Single Visit':
        return [
          { label: this.dictionaryService.get('Customer') || 'Customer', value: activity.customer || '-' },
        ];
      case 'Double Visit':
        return [
          { label: this.dictionaryService.get('Rep') || 'Rep', value: activity.rep || '-' },
          { label: this.dictionaryService.get('Customer') || 'Customer', value: activity.customer || '-' },
          { label: this.dictionaryService.get('Speciality') || 'Speciality', value: activity.speciality || '-' },
          { label: this.dictionaryService.get('Center') || 'Center', value: activity.center || '-' },
        ];
    }
  }

  getWorkTypeLabel(workType: DailyActivity['workType']): string {
    return this.dictionaryService.get(workType) || workType;
  }

  getWorkTypeClass(workType: string): string {
    switch (workType) {
      case 'Single Visit':
        return 'type-single';
      case 'Double Visit':
        return 'type-double';
      case 'NFA':
        return 'type-nfa';
      default:
        return '';
    }
  }
}
